import React from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import PropTypes from 'prop-types';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { get } from 'lodash';
import FormikTextField from '../../../../../components/Fields/TextField';
import EditModeCard, { EditModeCardSection } from '../../../../../components/EditModeCard';
import { TP } from '../../../../../constants/consts';
import TextButton from '../../../../../components/TextButton';
import { colors, fontSize } from '../../../../../constants/styleConstants';
import theme from '../../../../../src/theme';
import ImageCard from '../../../../../components/EditComponents/ImageCard';
import { createEntity, deleteEntity, getLanguagesAction, getWholeList, updateOne } from '../../../../App/actions';
import { COMPANIES, COMPANY_DESCRIPTIONS, LANGUAGES_LIST } from '../../../../../constants/endpointConstants';
import {
  createCompanyDescriptionAction,
  getCompanyDescriptionsAction,
  updateCompanyAction,
  updateCompanyDescriptionAction,
  deleteCompanyDescriptionAction,
} from '../../../actions';
import { selectCompanyData, selectCompanyLoading, selectCompanyDescriptions } from '../../../selectors';
import UploadImageModal from '../../../../../components/EditComponents/UploadImageModal';
import { getImageUrl, getUnusedLangsOptions } from '../../../../../utils/common';
import TitleBlock from '../../../../../components/EditComponents/TitleBlock';
import CompanyAbout from '../../../../../components/EditComponents/CompanyAbout';
import EditFormFooter from '../../../../../components/EditComponents/EditFormFooter';
import { selectLanguages } from '../../../../App/selectors';
import ConfirmDeleteModal from '../../../../../components/EditComponents/ConfirmDeleteModal';

export const basicInfoStyles = {
  imageBlock: {
    marginTop: '30px',
  },
  imagesButtonBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '18px',
  },
  addImageButton: {
    border: `1px solid rgba(151, 151, 151, 0.5)`,
    padding: '9px',
    width: '165px',
    color: colors.textColor,
    fontSize: fontSize.regularTitle,
    fontWeight: 'bold',
    justifyContent: 'space-between',
  },
  requestButton: {
    paddingTop: '12px',
    marginLeft: '10px',
    display: 'none',
  },
  noImages: {
    fontSize: fontSize.regularTitle,
    color: colors.websiteLinkColor,
    fontStyle: 'italic',
    marginBottom: theme.spacing(2),
  },
  errorMessage: {
    bottom: '-20px',
    position: 'absolute',
  },
  inputBlock: {
    paddingBottom: theme.spacing(1),
  },
  logoCard: {
    height: 92,
    minWidth: 92,
  },
};

const useStyles = makeStyles({
  ...basicInfoStyles,
  nameInput: {
    minWidth: '343px',
    [theme.breakpoints.down('xs')]: {
      minWidth: 'auto',
      maxWidth: '343px',
    },
  },
});

export const CompanyBasicInfo = ({ company, goToNextTab, logoLoading, languages, descriptions }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();

  React.useEffect(() => {
    if (languages?.length === 0) {
      dispatch(
        getWholeList({
          endpoint: LANGUAGES_LIST,
          sagaRoutine: getLanguagesAction,
        }),
      );
    }
    dispatch(
      getWholeList({
        endpoint: COMPANY_DESCRIPTIONS(company.id),
        sagaRoutine: getCompanyDescriptionsAction,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isNameDisabled, setNameDisabled] = React.useState(true);
  const [companyName, setCompanyName] = React.useState(company?.name);
  const [modalImageUrl, setModalImageUrl] = React.useState(null);
  const [idToDelete, setIdToDelete] = React.useState(null);

  const getUnusedLangs = React.useCallback(() => getUnusedLangsOptions(descriptions.data, languages), [
    languages,
    descriptions,
  ]);

  const submitName = () => {
    goToNextTab();
  };

  const submitLogo = fileData => {
    dispatch(
      updateOne({
        endpoint: COMPANIES,
        sagaRoutine: updateCompanyAction,
        id: company.id,
        params: { logo: { file: `@${fileData.filename}` } },
        files: { [fileData.filename]: fileData.file },
      }),
    );
    setModalImageUrl(null);
  };

  const addDescription = (data, callback) => {
    dispatch(
      createEntity({
        endpoint: COMPANY_DESCRIPTIONS(company.id),
        sagaRoutine: createCompanyDescriptionAction,
        params: {
          description: get(data, 'content'),
          language: { id: get(data, 'language.id') },
        },
        callback,
      }),
    );
  };

  const updateDescription = (data, callback) => {
    dispatch(
      updateOne({
        endpoint: COMPANY_DESCRIPTIONS(company.id),
        id: data.id,
        sagaRoutine: updateCompanyDescriptionAction,
        params: {
          description: get(data, 'content'),
          language: { id: get(data, 'language.id') },
        },
        callback,
      }),
    );
  };

  const deleteDescription = () => {
    dispatch(
      deleteEntity({
        endpoint: COMPANY_DESCRIPTIONS(company.id),
        id: idToDelete,
        sagaRoutine: deleteCompanyDescriptionAction,
        callback: () => setIdToDelete(null),
      }),
    );
  };

  return (
    <>
      <EditModeCard className={classes.inputBlock}>
        <EditModeCardSection first>
          <Grid container spacing={2}>
            <Grid item sm={5} xs={12} classes={{ root: classes.nameInput }}>
              <FormikTextField
                label={t(`${TP}.SORT_COMPANY_NAME`)}
                defaultValue={company?.name}
                disabled={isNameDisabled}
                onChange={e => setCompanyName(e.target?.value)}
                error={!companyName}
                helperText={companyName ? '' : t(`${TP}.FN_REQUIRED_FIELD`)}
                FormHelperTextProps={{ className: classes.errorMessage }}
              />
            </Grid>
            <TextButton className={classes.requestButton} onClick={() => setNameDisabled(!isNameDisabled)}>
              {t(`${TP}.FN_REQUEST_NAME_CHANGE`)}
            </TextButton>
          </Grid>
        </EditModeCardSection>
      </EditModeCard>
      <div className={classes.imageBlock}>
        <TitleBlock title={`${TP}.FN_COMPANY_LOGO_TITLE`} subtitle={`${TP}.FN_COMPANY_LOGO_SUBTITLE`} />
        <Divider />
        {company?.logo ? (
          <ImageCard
            image={company.logo}
            editFunc={imageUrl => setModalImageUrl(getImageUrl(imageUrl))}
            loading={logoLoading}
            contentClassName={classes.logoCard}
          />
        ) : (
          <div className={classes.imagesButtonBlock}>
            <span className={classes.noImages}>{t(`${TP}.FN_NO_IMAGES_ADDED`)}</span>
            <TextButton
              className={classes.addImageButton}
              endIcon={<AddIcon style={{ color: colors.blueLight }} />}
              onClick={() => setModalImageUrl(true)}
            >
              {t(`${TP}.FN_IMAGES_TITLE`)}
            </TextButton>
          </div>
        )}
      </div>
      <CompanyAbout
        descriptions={descriptions}
        languages={getUnusedLangs()}
        addDescription={addDescription}
        updateDescription={updateDescription}
        deleteDescription={setIdToDelete}
      />
      <EditFormFooter submitFunc={submitName} lastUpdatedDate={company?.updatedAt} />
      {modalImageUrl && (
        <UploadImageModal
          onClose={() => {
            setModalImageUrl(null);
          }}
          imageUrl={modalImageUrl}
          submitImage={submitLogo}
          title={`${TP}.FN_ADD_LOGO`}
        />
      )}
      {Boolean(idToDelete) && (
        <ConfirmDeleteModal
          closeModal={() => setIdToDelete(null)}
          title={t(`${TP}.FN_DELETE_DESCRIPTION_CONFIRMATION`)}
          onDelete={deleteDescription}
        />
      )}
    </>
  );
};

CompanyBasicInfo.propTypes = {
  company: PropTypes.object,
  goToNextTab: PropTypes.func,
};

CompanyBasicInfo.defaultProps = {
  company: {},
};

const mapStateToProps = createStructuredSelector({
  company: selectCompanyData(),
  logoLoading: selectCompanyLoading(),
  languages: selectLanguages(),
  descriptions: selectCompanyDescriptions(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(CompanyBasicInfo);
